import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TestimonialCarousel = ({ testimonials }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(3);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const carouselRef = useRef(null);

    // Update items per page based on screen size
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 640) {
                setItemsPerPage(1); // Mobile: 1 item
            } else if (window.innerWidth < 1024) {
                setItemsPerPage(2); // Tablet: 2 items
            } else {
                setItemsPerPage(3); // Desktop: 3 items
            }
        };

        handleResize(); // Set initial value
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const totalPages = Math.ceil(testimonials.length / itemsPerPage);

    const nextSlide = useCallback(() => {
        if (isTransitioning) return;

        setIsTransitioning(true);
        setCurrentIndex(prev => {
            const nextIndex = prev + itemsPerPage;
            return nextIndex >= testimonials.length ? 0 : nextIndex;
        });

        // Reset transition flag after animation completes
        setTimeout(() => {
            setIsTransitioning(false);
        }, 500);
    }, [itemsPerPage, testimonials.length, isTransitioning]);

    const prevSlide = useCallback(() => {
        if (isTransitioning) return;

        setIsTransitioning(true);
        setCurrentIndex(prev => {
            const prevIndex = prev - itemsPerPage;
            return prevIndex < 0 ? Math.max(0, testimonials.length - itemsPerPage) : prevIndex;
        });

        // Reset transition flag after animation completes
        setTimeout(() => {
            setIsTransitioning(false);
        }, 500);
    }, [itemsPerPage, testimonials.length, isTransitioning]);

    const goToSlide = (pageIndex) => {
        if (isTransitioning) return;

        setIsTransitioning(true);
        setCurrentIndex(pageIndex * itemsPerPage);

        setTimeout(() => {
            setIsTransitioning(false);
        }, 500);
    };

    // Touch handlers for mobile swipe
    const handleTouchStart = (e) => {
        if (isTransitioning) return;
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        if (isTransitioning) return;
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (isTransitioning || !touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextSlide();
        }
        if (isRightSwipe) {
            prevSlide();
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    // Calculate the width for each item based on items per page
    const getItemWidth = () => {
        switch (itemsPerPage) {
            case 1: return '100%';
            case 2: return '50%';
            case 3: return '33.333%';
            default: return '33.333%';
        }
    };

    //  const getItemWidth = (index) => {
    //   const remainingItems = testimonials.length - currentIndex;

    //   // If last page and fewer items than itemsPerPage
    //   if (remainingItems < itemsPerPage) {
    //     return `${100 / remainingItems}%`;
    //   }

    //   return `${100 / itemsPerPage}%`;
    // };


    return (
        <div className="relative w-full px-6 sm:px-8 md:px-10">
            {/* Main Carousel Container */}
            <div
                className="overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                ref={carouselRef}
            >
                <div
                    className="flex transition-transform duration-500 ease-in-out py-6"
                    style={{
                        transform: `translateX(-${(currentIndex / itemsPerPage) * 100}%)`,
                    }}
                >
                    {testimonials.map((testimonial, index) => (
                        <div
                            key={testimonial.id}
                            className="flex-shrink-0 px-2 sm:px-3"
                            style={{ width: getItemWidth() }}
                        >
                            <TestimonialCard testimonial={testimonial} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Navigation Controls */}
            {totalPages > 1 && (
                <>
                    {/* Previous Button */}
                    <button
                        onClick={prevSlide}
                        disabled={isTransitioning}
                        className={`absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-110 transition-all duration-300 border border-gray-200 z-10 -ml-2 sm:-ml-4`}
                        aria-label="Previous slide"
                    >
                        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    </button>

                    {/* Next Button */}
                    <button
                        onClick={nextSlide}
                        disabled={isTransitioning}
                        className={`absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-110 transition-all duration-300 border border-gray-200 z-10 -mr-2 sm:-mr-4`}
                        aria-label="Next slide"
                    >
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    </button>

                    {/* Pagination Dots */}
                    <div className="flex justify-center gap-2 mt-6 md:mt-8">
                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                disabled={isTransitioning}
                                className={`h-2 rounded-full transition-all duration-300 ${Math.floor(currentIndex / itemsPerPage) === index
                                    ? 'w-6 sm:w-8 bg-gradient-to-r from-indigo-600 to-purple-600'
                                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>

                    {/* Slide Counter */}
                    <div className="hidden sm:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-12 text-sm text-gray-400">
                        {Math.floor(currentIndex / itemsPerPage) + 1} / {totalPages}
                    </div>
                </>
            )}
        </div>
    );
};


// Individual Testimonial Card Component - Modern with Subtle Interactions
// const TestimonialCard = ({ testimonial }) => {
//     const [imageError, setImageError] = useState(false);

//     // Platform colors for subtle accents
//     const getPlatformColor = () => {
//         switch (testimonial.platform) {
//             case 'Instagram': return 'text-pink-600 bg-pink-50 border-pink-100';
//             case 'TikTok': return 'text-gray-900 bg-gray-100 border-gray-200';
//             case 'YouTube': return 'text-red-600 bg-red-50 border-red-100';
//             case 'Twitter': return 'text-blue-500 bg-blue-50 border-blue-100';
//             case 'Facebook': return 'text-blue-600 bg-blue-50 border-blue-100';
//             default: return 'text-indigo-600 bg-indigo-50 border-indigo-100';
//         }
//     };

//     const getAvatarInitials = (name) => {
//         if (!name) return "";

//         const names = name.trim().split(" ");
//         const first = names[0]?.charAt(0) || "";
//         const last = names.length > 1 ? names[names.length - 1].charAt(0) : "";

//         return (first + last).toUpperCase();
//     };


//     return (
//         <div className="h-full group">
//             <div className="relative h-full bg-white rounded-2xl p-6 
//                           border border-gray-100 
//                           shadow-sm group-hover:shadow-lg 
//                           transition-all duration-300">

//                 {/* Subtle gradient line on top */}
//                 <div className="absolute top-0 left-4 right-4 h-0.5 
//                               bg-gradient-to-r from-transparent via-indigo-200 to-transparent 
//                               opacity-0 group-hover:opacity-100 
//                               transition-opacity duration-300"></div>

//                 {/* Header */}
//                 <div className="flex items-start justify-between mb-4">
//                     {/* Platform Badge - Subtle */}
//                     {testimonial.platform && (
//                         <span className={`inline-flex items-center px-3 py-1 
//                                        text-xs font-medium rounded-full
//                                        ${getPlatformColor()}`}>
//                             {testimonial.platform}
//                         </span>
//                     )}

//                     {/* Rating - Clean Stars */}
//                     <div className="flex gap-0.5">
//                         {[...Array(5)].map((_, index) => (
//                             <svg
//                                 key={index}
//                                 className={`w-4 h-4 transition-colors duration-200 ${index < testimonial.rating
//                                     ? 'text-amber-400'
//                                     : 'text-gray-200'
//                                     }`}
//                                 fill="currentColor"
//                                 viewBox="0 0 20 20"
//                             >
//                                 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
//                             </svg>
//                         ))}
//                     </div>
//                 </div>

//                 {/* Quote Icon - Very Subtle */}
//                 <div className="mb-3 opacity-30 group-hover:opacity-40 transition-opacity">
//                     <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
//                         <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
//                     </svg>
//                 </div>

//                 {/* Testimonial Text */}
//                 <p className="text-gray-600 text-base leading-relaxed mb-6 line-clamp-5">
//                     "{testimonial.content}"
//                 </p>

//                 {/* Author Section */}
//                 <div className="flex items-center gap-3">
//                     {/* Avatar */}
//                     {/* <div className="flex-shrink-0">
//                         {testimonial.image && !imageError ? (
//                             <img
//                                 src={testimonial.image}
//                                 alt={testimonial.name}
//                                 className="w-10 h-10 rounded-full object-cover 
//                                          ring-2 ring-white group-hover:ring-indigo-50 
//                                          transition-all duration-300"
//                                 onError={() => setImageError(true)}
//                                 loading="lazy"
//                             />
//                         ) : (
//                             <div className="w-10 h-10 rounded-full 
//                                           bg-gradient-to-br from-gray-100 to-gray-200 
//                                           flex items-center justify-center
//                                           text-gray-600 font-medium
//                                           ring-2 ring-white group-hover:ring-indigo-50 
//                                           transition-all duration-300">
//                                 {getAvatarInitials(testimonial.name)}
//                             </div>
//                         )}
//                     </div> */}

//                     <div className="relative">
//                         {/* Avatar ring */}
//                         <div className="absolute -inset-1 bg-gradient-to-r 
//                                           from-indigo-500 to-purple-500 rounded-full 
//                                           opacity-0 group-hover:opacity-50 
//                                           blur-sm transition-opacity duration-500"></div>

//                         {testimonial.image && !imageError ? (
//                             <img
//                                 src={testimonial.image}
//                                 alt={testimonial.name}
//                                 className="relative w-11 h-11 rounded-full 
//                                              object-cover border-2 border-white"
//                                 onError={() => setImageError(true)}
//                                 loading="lazy"
//                             />
//                         ) : (
//                             <div className="relative w-11 h-11 rounded-full 
//                                               bg-gradient-to-br from-indigo-100 to-purple-100 
//                                               flex items-center justify-center
//                                               text-indigo-500 font-semibold text-base
//                                               border-2 border-white">
//                                 {getAvatarInitials(testimonial.name)}
//                             </div>
//                         )}
//                     </div>


//                     {/* Author Details */}
//                     <div className="flex-1 min-w-0">
//                         <div className="flex items-center gap-2">
//                             <h4 className="font-medium text-gray-900 text-sm truncate">
//                                 {testimonial.name}
//                             </h4>
//                             {testimonial.verified && (
//                                 <svg className="w-4 h-4 text-indigo-500 flex-shrink-0"
//                                     fill="currentColor" viewBox="0 0 20 20">
//                                     <path fillRule="evenodd"
//                                         d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812z"
//                                         clipRule="evenodd" />
//                                 </svg>
//                             )}
//                         </div>
//                         <p className="text-xs text-gray-500 truncate">
//                             {testimonial.role} · {testimonial.company}
//                         </p>
//                     </div>
//                 </div>

//                 {/* Subtle hover indicator - bottom line */}
//                 <div className="absolute bottom-0 left-1/2 -translate-x-1/2 
//                               w-0 group-hover:w-12 h-0.5 bg-gradient-to-r 
//                               from-indigo-400 to-purple-400 rounded-full 
//                               transition-all duration-300"></div>
//             </div>
//         </div>
//     );
// };


const TestimonialCard = ({ testimonial }) => {
    const [imageError, setImageError] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // Platform colors with more vibrant options
    const getPlatformColor = () => {
        switch (testimonial.platform) {
            case 'Instagram': return {
                badge: 'text-pink-600 bg-pink-50 border-pink-100',
                light: 'bg-pink-50 text-pink-600',
                gradient: 'from-pink-500 via-purple-500 to-indigo-500',
                accent: 'pink-500',
            };
            case 'TikTok': return {
                badge: 'text-purple-600 bg-purple-100 border-purple-200',
                light: 'bg-purple-100 text-purple-900',
                gradient: 'from-gray-900 to-purple-600',
                accent: 'purple-500',
            };
            case 'Pinterest': return {
                badge: 'text-red-600 bg-red-50 border-red-100',
                light: 'bg-red-50 text-red-600',
                gradient: 'from-red-500 to-rose-500',
                accent: 'red-500',
            };
            case 'Twitter': return {
                badge: 'text-blue-500 bg-blue-50 border-blue-100',
                light: 'bg-blue-50 text-blue-500',
                gradient: 'from-blue-400 to-blue-600',
                accent: 'blue-400',
            };
            case 'Facebook': return {
                badge: 'text-blue-600 bg-blue-50 border-blue-100',
                light: 'bg-blue-50 text-blue-600',
                gradient: 'from-blue-600 to-indigo-700',
                accent: 'blue-600',
            };
            default: return {
                badge: 'text-indigo-600 bg-indigo-50 border-indigo-100',
                light: 'bg-indigo-50 text-indigo-600',
                gradient: 'from-blue-600 to-indigo-700',
                accent: 'indigo-600',
            };
        }
    };

    const getAvatarInitials = (name) => {
        if (!name) return "";
        const names = name.trim().split(" ");
        const first = names[0]?.charAt(0) || "";
        const last = names.length > 1 ? names[names.length - 1].charAt(0) : "";
        return (first + last).toUpperCase();
    };

    const style = getPlatformColor();

    return (
        <div
            className="h-full group perspective-1000"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className={`relative h-full bg-white rounded-2xl p-6 
                          border border-slate-200
                          shadow-sm hover:shadow-xl
                          transition-all duration-500 ease-out
                          overflow-hidden`}>

                {/* Animated gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${style.gradient} 
                              opacity-0 ${isHovered ? 'opacity-5' : ''} 
                              transition-opacity duration-700`}></div>


                {/* Corner decoration */}
                <div className={`absolute top-0 right-0 w-16 h-16 
                              bg-gradient-to-br ${style.gradient} 
                              rounded-bl-full transform 
                              ${isHovered ? 'translate-x-0 translate-y-0 rotate-0' : 'translate-x-8 -translate-y-8 rotate-45'} 
                              opacity-10 transition-all duration-700`}></div>

                {/* Header */}
                <div className="relative flex items-start justify-between mb-4 z-10">
                    {/* Platform Badge - Updated subtle styling */}
                    {testimonial.platform && (
                        <div className="group/badge">
                            <span className={`relative inline-flex items-center gap-1.5 px-4 py-1.5 
                                           text-xs font-medium rounded-full border
                                           ${style.badge} shadow-sm
                                           transform group-hover:scale-105 
                                           transition-all duration-300 cursor-default`}>
                                <span>{testimonial.platform}</span>
                            </span>
                        </div>
                    )}

                    {/* Rating Stars with animation */}
                    <div className="flex gap-1">
                        {[...Array(5)].map((_, index) => (
                            <div key={index} className="relative group/star">
                                <div className={`absolute -inset-1 bg-yellow-400/20 
                                              rounded-full blur-sm opacity-0 
                                              group-hover/star:opacity-100 
                                              transition-opacity duration-300`}></div>
                                <svg
                                    className={`w-4 h-4 transform transition-all duration-300
                                              ${index < testimonial.rating
                                            ? 'text-amber-400 scale-100 group-hover/star:scale-125'
                                            : 'text-gray-200 scale-90'}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Quote Icon with animation */}
                <div className={`mb-3 transform transition-all duration-500
                              ${isHovered ? `translate-x-2  text-${style.accent} opacity-60` : 'text-gray-300'}`}>
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                </div>

                {/* Testimonial Text with highlight on hover */}
                <div className="relative mb-6 group/text">
                    <div className={`absolute -inset-2 bg-gradient-to-r ${style.gradient} 
                                  rounded-xl opacity-0 ${isHovered ? 'opacity-5' : ''} 
                                  transition-opacity duration-500`}></div>
                    <p className="relative text-gray-700 text-base leading-relaxed line-clamp-5
                                group-hover:text-gray-900 transition-colors duration-300">
                        "{testimonial.content}"
                    </p>
                </div>

                {/* Author Section with enhanced interactivity */}
                <div className="relative flex items-center gap-3 mt-auto">
                    {/* Avatar with ring animation */}
                    <div className="relative group/avatar">
                        <div className={`absolute -inset-1.5 bg-gradient-to-r ${style.gradient} 
                                      rounded-full opacity-0 ${isHovered ? 'opacity-75' : ''} 
                                      animate-spin-slow transition-opacity duration-500`}></div>
                        <div className={`absolute -inset-2 bg-gradient-to-r ${style.gradient} 
                                      rounded-full opacity-0 ${isHovered ? 'opacity-50' : ''} 
                                      animate-spin-slower transition-opacity duration-500`}></div>

                        {testimonial.image && !imageError ? (
                            <img
                                src={testimonial.image}
                                alt={testimonial.name}
                                className={`relative w-12 h-12 rounded-xl 
                                         object-cover border-2 border-white shadow-lg
                                         transform transition-all duration-500
                                         ${isHovered ? 'scale-110 -rotate-3' : ''}`}
                                onError={() => setImageError(true)}
                                loading="lazy"
                            />
                        ) : (
                            <div className={`relative w-12 h-12 rounded-xl 
                                          bg-gradient-to-br ${style.gradient} 
                                          flex items-center justify-center
                                          text-white font-bold text-base
                                          border-2 border-white shadow-lg
                                          transform transition-all duration-500
                                          ${isHovered ? 'scale-110 -rotate-3' : ''}`}>
                                {getAvatarInitials(testimonial.name)}
                            </div>
                        )}
                    </div>

                    {/* Author Details */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h4 className={`font-bold text-gray-900 text-base truncate
                                         transition-all duration-300
                                         ${isHovered ? `bg-gradient-to-r ${style.gradient} bg-clip-text text-transparent` : ''}`}>
                                {testimonial.name}
                            </h4>
                            {testimonial.verified && (
                                <div className="relative group/verified">
                                    <div className="w-5 h-5 bg-green-500 rounded-full 
                                                  flex items-center justify-center shadow-lg
                                                  transform group-hover/verified:scale-110 
                                                  transition-transform duration-300">
                                        <svg className="w-3 h-3 text-white"
                                            fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    {/* Tooltip */}
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 
                                                  bg-gray-900 text-white text-xs py-1 px-2 rounded 
                                                  whitespace-nowrap opacity-0 group-hover/verified:opacity-100
                                                  transition-opacity duration-200 pointer-events-none">
                                        Verified Creator
                                    </div>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 truncate flex items-center gap-2">
                            <span>{testimonial.role}</span>
                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                            <span className={`${isHovered ? `text-${style.accent}` : 'text-gray-500'} 
                                           transition-colors duration-300`}>
                                {testimonial.company}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TestimonialCarousel;

export const testimonials = [
    {
        id: 1,
        name: "Marcus Johnson",
        role: "Digital Marketing Director",
        company: "Nike",
        content: "SocialTV completely transformed how we repurpose our Instagram content. We're now running premium CTV ads that look incredible on big screens, all created in minutes instead of days.",
        rating: 5,
        avatarColor: "from-pink-500 to-orange-500",
        platform: "Instagram",
        image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 2,
        name: "Sarah Liu",
        role: "Marketing Manager",
        company: "Shopify",
        content: "The VAST tag generation is seamless. We're running our Facebook video ads on Roku and Fire TV with zero technical headaches. Game changer for our small team.",
        rating: 5,
        avatarColor: "from-blue-500 to-cyan-500",
        platform: "Facebook",
        image: "https://images.unsplash.com/photo-1494790108777-2fd0db25a84d?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 3,
        name: "David Rodriguez",
        role: "Content Creator",
        company: "2M+ followers",
        content: "The 4K quality is outstanding. Our TikTok videos look better on TV than they do on the app! Plus, the analytics help us track exactly how our campaigns are performing.",
        rating: 5,
        avatarColor: "from-purple-500 to-pink-500",
        platform: "TikTok",
        image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 4,
        name: "Emma Parker",
        role: "Media Buyer",
        company: "Zenith Media",
        content: "We've increased our CTV reach by 300% while cutting production time by 80%. The templates are gorgeous and the platform is incredibly intuitive.",
        rating: 5,
        avatarColor: "from-green-500 to-emerald-500",
        platform: "Multiple",
        image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 5,
        name: "Thomas Chen",
        role: "Agency Director",
        company: "Creative Digital",
        content: "As an agency handling multiple clients, SocialTV has become an essential tool in our workflow. The ability to quickly turn social content into CTV-compatible ads across different brands has streamlined our entire production process. Highly recommended!",
        rating: 5,
        avatarColor: "from-indigo-400 to-purple-400",
        platform: "All Platforms",
        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 6,
        name: "Lisa Wang",
        role: "Brand Director",
        company: "Zara",
        content: "The AI-powered formatting saves us hours of work every week. Our social team can now focus on creating great content while SocialTV handles the technical aspects of CTV distribution.",
        rating: 5,
        avatarColor: "from-red-500 to-pink-500",
        platform: "Pinterest",
        image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 7,
        name: "James Wilson",
        role: "Performance Marketer",
        company: "Netflix",
        content: "Finally, a tool that bridges the gap between social and CTV. The analytics integration is perfect for our data-driven approach. We're seeing 4x ROAS on our campaigns.",
        rating: 5,
        avatarColor: "from-blue-600 to-indigo-600",
        platform: "X (Twitter)",
        image: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 8,
        name: "Maria Garcia",
        role: "Creative Director",
        company: "Meta",
        content: "The template library is incredible. We can maintain brand consistency across all our CTV campaigns while still having the flexibility to customize for different platforms.",
        rating: 5,
        avatarColor: "from-purple-600 to-indigo-600",
        platform: "Instagram",
        image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face"
    },
    {
        id: 9,
        name: "Thomas Chen",
        role: "Agency Director",
        company: "Creative Digital",
        content: "As an agency handling multiple clients, SocialTV has become an essential tool in our workflow. The ability to quickly turn social content into CTV-compatible ads across different brands has streamlined our entire production process. Highly recommended!",
        rating: 5,
        avatarColor: "from-indigo-400 to-purple-400",
        platform: "All Platforms",
        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
];